import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ConsultationFieldDefinition } from '../types/Consultation';
import {
  createEmptyRepeaterValue,
  parseConsultationRepeaterSchema,
  parseRepeaterValue,
  repeaterSchemaHelpers,
  serializeRepeaterValue,
  type ConsultationRepeaterFieldSchema,
  type ConsultationRepeaterSchema,
} from '../consultationRepeaterSchema';

interface ConsultationRepeaterFieldInputProps {
  field: ConsultationFieldDefinition;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

type RepeaterRow = Record<string, unknown>;

const createEmptyRow = (fields: ConsultationRepeaterFieldSchema[]): RepeaterRow => {
  const row: RepeaterRow = {};
  fields.forEach((childField) => {
    if (childField.type === 'repeater') {
      row[childField.key] = [];
      return;
    }

    if (childField.type === 'boolean') {
      row[childField.key] = 'false';
      return;
    }

    row[childField.key] = '';
  });

  return row;
};

const renderScalarInput = (
  childField: ConsultationRepeaterFieldSchema,
  value: string,
  onChange: (value: string) => void,
  disabled: boolean,
) => {
  switch (childField.type) {
    case 'longText':
      return (
        <textarea
          className="text-input"
          rows={3}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'number':
    case 'decimal':
      return (
        <input
          type="number"
          step={childField.type === 'decimal' ? '0.01' : '1'}
          className="text-input"
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case 'boolean':
      return (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={value === 'true'}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked ? 'true' : 'false')}
          />
          <span>{childField.label ?? childField.key}</span>
        </label>
      );
    case 'date':
      return (
        <input type="date" className="text-input" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      );
    case 'time':
      return (
        <input type="time" className="text-input" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      );
    case 'dateTime':
      return (
        <input type="datetime-local" className="text-input" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      );
    case 'singleSelect':
      return (
        <select className="text-input" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
          <option value="">-</option>
          {(childField.options ?? []).map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
      );
    case 'multiSelect': {
      const selected = value ? value.split(',').map((item) => item.trim()).filter(Boolean) : [];
      const toggle = (optionId: string) => {
        const next = selected.includes(optionId)
          ? selected.filter((item) => item !== optionId)
          : [...selected, optionId];
        onChange(next.join(','));
      };

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(childField.options ?? []).map((option) => (
            <label key={option.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={selected.includes(option.id)} disabled={disabled} onChange={() => toggle(option.id)} />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      );
    }
    default:
      return (
        <input type="text" className="text-input" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      );
  }
};

const NestedRepeaterSection: React.FC<{
  schema: ConsultationRepeaterSchema;
  items: RepeaterRow[];
  onChange: (items: RepeaterRow[]) => void;
  disabled?: boolean;
}> = ({ schema, items, onChange, disabled = false }) => {
  const { t } = useTranslation('consultations');
  const itemLabel = schema.itemLabel ?? t('repeaterItem', { defaultValue: 'item' });

  const addItem = () => {
    onChange([...items, createEmptyRow(schema.fields)]);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, itemIndex) => itemIndex !== index));
  };

  const updateItem = (index: number, nextItem: RepeaterRow) => {
    onChange(items.map((item, itemIndex) => (itemIndex === index ? nextItem : item)));
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {items.map((item, index) => (
        <div
          key={`${schema.itemLabel ?? 'item'}-${index}`}
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: 12,
            display: 'grid',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={disabled}
              onClick={() => removeItem(index)}
            >
              {t('repeaterRemove', { defaultValue: 'Remove' })}
            </button>
          </div>

          {schema.fields.map((childField) => {
            if (childField.type === 'repeater') {
              const nestedItems = Array.isArray(item[childField.key]) ? (item[childField.key] as RepeaterRow[]) : [];
              return (
                <div key={childField.key} style={{ display: 'grid', gap: 8 }}>
                  <label>{childField.label ?? childField.key}</label>
                  <NestedRepeaterSection
                    schema={{
                      fields: childField.fields ?? [],
                      minItems: childField.minItems,
                      maxItems: childField.maxItems,
                      itemLabel: childField.itemLabel,
                    }}
                    items={nestedItems}
                    disabled={disabled}
                    onChange={(nextItems) => updateItem(index, { ...item, [childField.key]: nextItems })}
                  />
                </div>
              );
            }

            const scalarValue = typeof item[childField.key] === 'string'
              ? String(item[childField.key])
              : item[childField.key] === true
                ? 'true'
                : item[childField.key] === false
                  ? 'false'
                  : '';

            return (
              <div key={childField.key} style={{ display: 'grid', gap: 6 }}>
                <label>{childField.label ?? childField.key}</label>
                {renderScalarInput(
                  childField,
                  scalarValue,
                  (nextValue) => updateItem(index, { ...item, [childField.key]: nextValue }),
                  disabled,
                )}
              </div>
            );
          })}
        </div>
      ))}

      <button type="button" className="btn btn-secondary" disabled={disabled} onClick={addItem}>
        {t('repeaterAddItem', { item: itemLabel, defaultValue: `Add ${itemLabel}` })}
      </button>
    </div>
  );
};

export const ConsultationRepeaterFieldInput: React.FC<ConsultationRepeaterFieldInputProps> = ({
  field,
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation('consultations');
  const schema = React.useMemo(
    () => parseConsultationRepeaterSchema(field.schemaJson),
    [field.schemaJson],
  );

  const parsedValue = React.useMemo(
    () => (schema ? parseRepeaterValue(schema, value) : createEmptyRepeaterValue({ fields: [] })),
    [schema, value],
  );

  if (!schema) {
    return (
      <div style={{ color: '#b45309' }}>
        {t('repeaterInvalidSchema', { defaultValue: 'Repeater schema is missing or invalid for this field.' })}
      </div>
    );
  }

  const items = repeaterSchemaHelpers.getItemsArray(parsedValue, schema) as RepeaterRow[];

  const updateItems = (nextItems: RepeaterRow[]) => {
    onChange(serializeRepeaterValue(repeaterSchemaHelpers.setItemsArray(parsedValue, schema, nextItems)));
  };

  return (
    <NestedRepeaterSection
      schema={schema}
      items={items}
      disabled={disabled}
      onChange={updateItems}
    />
  );
};

export default ConsultationRepeaterFieldInput;
